import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "rsuite";

import { useInput } from "hooks/useInput";
import { createFilterProps, getDefaultInputValue } from "utility/form";

import { Dialog } from "components/common/dialog/Dialog";

import { renderConditionalProp } from "utility";
import { debounce } from "utility/debounce";
import { FilterProps } from "store/common";

import { useRootDispatch } from "store/hooks";
import {
  clearFilter,
  setFilters,
} from "store/accountSummary/accountSummary-slice";
import { useRequestDispatch } from "store/requestHooks";
import { FetchAccountSummary, fetchAccountSummary } from "store/accountSummary/accountSummary-action";

import classes from "./Filters.module.scss";

export interface FiltersProps {
  setOpenDialog: (close: boolean) => void;
  filters?: FilterProps | null;
  slider?: boolean;
  contentOnly?: boolean;
  filterEvent?: any;
}

export const Filters: React.FC<FiltersProps> = ({
  setOpenDialog,
  filters,
  slider,
  contentOnly,
  filterEvent,
}) => {
  const { t } = useTranslation(["common", "account", "payment_final"]);

  const searchKeyword = useInput(getDefaultInputValue(filters?.searchKeyword));

  const [startDate, handleStartChange] = useState<Date | null>(
    filters?.minDate ? new Date(filters.minDate) : null
  );
  const [endDate, handleEndChange] = useState<Date | null>(
    filters?.maxDate ? new Date(filters.maxDate) : null
  );

  const dueDate = useInput(
    getDefaultInputValue(filters?.dueDate, 0)
  );
  const pastDue = useInput(
    getDefaultInputValue(filters?.pastDue, 0)
  );
  const dueIn5days = useInput(
    getDefaultInputValue(filters?.dueIn5days, 0)
  );
  const autoPay = useInput(
    getDefaultInputValue(filters?.autoPay, 0)
  );

  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();

  const resetButtonHandler = () => {
    dispatch(clearFilter());
    requestDispatch<FetchAccountSummary, FilterProps>(fetchAccountSummary, {});
  };

  const applyFilterHandler = () => {

    const filters: FilterProps = {};

    if (startDate) filters.minDate = startDate.toDateString()
    if (endDate) filters.maxDate = endDate.toDateString()
    if (searchKeyword) filters.searchKeyword = searchKeyword.value
    if (dueDate) filters.dueDate = dueDate.value
    if (pastDue) filters.pastDue = pastDue.value
    if (autoPay) filters.autoPay = autoPay.value
    if (dueIn5days) filters.dueIn5days = dueIn5days.value

    const filterDTO = createFilterProps(filters);

    if (filterDTO) {
      dispatch(setFilters(filters));
      requestDispatch<FetchAccountSummary, FilterProps>(fetchAccountSummary, filters);
    }
  };

  useEffect(() => {
    const filterValue = {
      searchKeyword: searchKeyword.value,
      pastDue: pastDue.value,
      dueIn5days: dueIn5days.value,
    };

    filterEvent && filterEvent(filterValue);
  }, [searchKeyword.value, pastDue.value, dueIn5days.value, filterEvent]);

  const datePickerOpenHandler = () => {
    const datePicker = document.getElementsByClassName("rs-picker-menu");
    datePicker[0] && datePicker[0].removeAttribute("inert");
  };

  const dialogContent = () => (
    <section className={classes.Wrapper}>
      {!slider && (
        <p>
          Search <span className={classes.SearcKeywordText}>(Account)</span>
        </p>
      )}
      <div
        className={[classes.FieldWrapper, classes.InlineFieldWrapper].join(" ")}
      >
        <ft-textfield
          outlined
          label={t("payment_final:search")}
          value={searchKeyword.value}
          ref={searchKeyword.ref}
          onInput={debounce(searchKeyword.valueChangeHandler, 300)}
          onBlur={searchKeyword.inputBlurHandler}
        ></ft-textfield>
      </div>
      {!slider && (
        <>
          <ft-divider></ft-divider>
          <p>Date Range</p>
          <div className={["rsuite-custom", classes.FieldWrapper].join(" ")}>
            <DatePicker
              isoWeek
              onOpen={datePickerOpenHandler}
              block
              oneTap
              placeholder={t("payment_final:start_date")}
              value={startDate}
              onChange={handleStartChange}
              size="lg"
            />
          </div>
          <div className={["rsuite-custom", classes.FieldWrapper].join(" ")}>
            <DatePicker
              isoWeek
              onOpen={datePickerOpenHandler}
              block
              oneTap
              placeholder={t("payment_final:end_date")}
              value={endDate}
              onChange={handleEndChange}
              size="lg"
            />
          </div>
          <ft-divider></ft-divider>

          <p>{t("payment_final:due_date_status")}</p>
        </>
      )}
      <div
        className={[classes.FieldWrapper, classes.InlineFieldWrapper].join(" ")}
      >
        {!slider && (
          <ft-formfield class={classes.Tnc} label={t("payment_final:due_date_status")}>
            <ft-checkbox
              onClick={dueDate.valueCheckedHandler}
              {...renderConditionalProp(dueDate.value, "checked")}
            ></ft-checkbox>
          </ft-formfield>
        )}
        <ft-formfield class={classes.Tnc} label={t("payment_final:past_due")}>
          <ft-checkbox
            onClick={pastDue.valueCheckedHandler}
            {...renderConditionalProp(pastDue.value, "checked")}
          ></ft-checkbox>
        </ft-formfield>
        <ft-formfield class={classes.Tnc} label={t("payment_final:due_in_five_days")}>
          <ft-checkbox
            onClick={dueIn5days.valueCheckedHandler}
            {...renderConditionalProp(dueIn5days.value, "checked")}
          ></ft-checkbox>
        </ft-formfield>
        {!slider && (
          <ft-formfield class={classes.Tnc} label={t("common:autopay_label")}>
            <ft-checkbox
              onClick={autoPay.valueCheckedHandler}
              {...renderConditionalProp(autoPay.value, "checked")}
            ></ft-checkbox>
          </ft-formfield>
        )}
      </div>
      {!slider && (
        <>
          <ft-divider></ft-divider>
          <div className={classes.ButtonWrapper}>
            <ft-button
              onClick={resetButtonHandler}
              class={[classes.ResetFilter, "outline-button"].join(" ")}
              label={t("payment_final:reset_filter")}
              outlined
            ></ft-button>
            <ft-button
              class={classes.ApplyFilter}
              label={t("payment_final:apply_filter")}
              unelevated
              onClick={applyFilterHandler}
            ></ft-button>
          </div>
        </>
      )}
    </section>
  );

  return (
    <>
      {contentOnly ? (
        dialogContent()
      ) : (
        <Dialog
          hideIndication
          hideActions
          closeIcon
          dialogClosed={setOpenDialog}
        >
          {dialogContent()}
        </Dialog>
      )}
    </>
  );
};
